import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Copy,
  MoreVertical,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import {
  contourPageSize,
  contourPercent,
  createContourComment,
  createContourCategory,
  createContourStage,
  createContourTheme,
  deleteContourCategory,
  deleteContourComment,
  deleteContourStage,
  deleteContourTheme,
  duplicateContourTheme,
  formatContourProgress,
  formatThemeCount,
  getContourCategories,
  getContourCategory,
  getContourAssignees,
  getContourComments,
  getContourStages,
  getContourSummary,
  getContourTaskHistory,
  getContourTheme,
  getContourThemes,
  reorderContourCategoryPage,
  reorderContourStagePage,
  reorderContourThemePage,
  searchContour,
  updateContourCategory,
  updateContourStage,
  updateContourTheme,
  type ContourCategory,
  type ContourComment,
  type ContourOverview,
  type ContourPage,
  type ContourSearchResult,
  type ContourStage,
  type ContourTaskHistory,
  type ContourTaskMetadata,
  type ContourTheme,
} from '../api/contour';
import { ApiError } from '../api/client';
import { StageList } from '../components/contour/StageList';
import { LoadingState, StatePanel } from '../components/StatePanel';

const CATEGORY_ICONS = ['🏗', '🔥', '⚡', '💧', '🚂', '🏢', '📄', '🔧'];
const THEME_ICONS = ['🔥', '⚡', '💧', '🌬', '🚂', '🏗', '🏢', '🔧'];

type View =
  | { kind: 'root' }
  | { kind: 'category'; categoryId: string }
  | { kind: 'theme'; categoryId: string; themeId: string };

/** Горизонтальный прогресс-бар. Зелёный на 100%, синий во всех прочих случаях. */
function ProgressBar({ completed, total }: { completed: number; total: number }) {
  const percent = contourPercent(completed, total);
  return (
    <div
      className={`contour-progress ${percent === 100 && total > 0 ? 'contour-progress--done' : ''}`}
      role="progressbar"
      aria-valuenow={percent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <span className="contour-progress__fill" style={{ width: `${percent}%` }} />
    </div>
  );
}

function PageControls({ page, total, pageSize, onPage, label = 'Страницы списка' }: {
  page: number; total: number; pageSize: number; onPage: (page: number) => void; label?: string;
}) {
  const lastPage = Math.ceil(total / pageSize);
  if (lastPage <= 1) return null;
  return (
    <nav className="contour-pages" aria-label={label}>
      <button className="button button--secondary" type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>
        Назад
      </button>
      <span aria-live="polite">{page} из {lastPage}</span>
      <button className="button button--secondary" type="button" disabled={page >= lastPage} onClick={() => onPage(page + 1)}>
        Далее
      </button>
    </nav>
  );
}

/** Модальное окно поверх существующего .modal-backdrop. */
function ContourModal({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    document.body.classList.add('modal-open');
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.classList.remove('modal-open');
      window.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section className="contour-dialog" role="dialog" aria-modal="true" aria-label={title}>
        <header className="contour-dialog__header">
          <h2>{title}</h2>
          <button className="icon-button icon-button--small" type="button" onClick={onClose} aria-label="Закрыть">
            <X size={18} />
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

/** Выбор эмодзи-иконки. */
function IconPicker({
  icons,
  value,
  onChange,
}: {
  icons: string[];
  value: string;
  onChange: (icon: string) => void;
}) {
  return (
    <div className="contour-icon-picker" role="radiogroup" aria-label="Иконка">
      {icons.map((icon) => (
        <button
          key={icon}
          type="button"
          role="radio"
          aria-checked={value === icon}
          className={`contour-icon-picker__item ${value === icon ? 'is-active' : ''}`}
          onClick={() => onChange(icon)}
        >
          <span aria-hidden="true">{icon}</span>
        </button>
      ))}
    </div>
  );
}

function HighlightMatch({ text, query }: { text: string; query: string }) {
  const matchAt = text.toLocaleLowerCase('ru-RU').indexOf(query.trim().toLocaleLowerCase('ru-RU'));
  if (matchAt < 0 || !query.trim()) return <>{text}</>;
  return <>{text.slice(0, matchAt)}<mark>{text.slice(matchAt, matchAt + query.trim().length)}</mark>{text.slice(matchAt + query.trim().length)}</>;
}

export function ContourPage() {
  const [overview, setOverview] = useState<ContourOverview | null>(null);
  const [categoryPage, setCategoryPage] = useState(1);
  const [themePage, setThemePage] = useState(1);
  const [stagePage, setStagePage] = useState(1);
  const [categoryTotal, setCategoryTotal] = useState(0);
  const [themeTotal, setThemeTotal] = useState(0);
  const [stageTotal, setStageTotal] = useState(0);
  const [searchPage, setSearchPage] = useState(1);
  const [searchRefresh, setSearchRefresh] = useState(0);
  const [searchResults, setSearchResults] = useState<ContourPage<ContourSearchResult> | null>(null);
  const [searchBusy, setSearchBusy] = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [loadState, setLoadState] = useState<'loading' | 'success' | 'error'>('loading');
  const [view, setView] = useState<View>({ kind: 'root' });
  const [editMode, setEditMode] = useState(false);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [highlightStageId, setHighlightStageId] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const busyRef = useRef(false);
  const loadGeneration = useRef(0);
  const searchGeneration = useRef(0);

  // Диалоги
  const [categoryDialog, setCategoryDialog] = useState<{ mode: 'create' | 'edit'; item?: ContourCategory } | null>(null);
  const [themeDialog, setThemeDialog] = useState<{ mode: 'create' | 'edit'; item?: ContourTheme } | null>(null);
  const [stageDialog, setStageDialog] = useState<{ mode: 'create' | 'edit'; item?: ContourStage } | null>(null);
  const [confirm, setConfirm] = useState<
    | { kind: 'category'; item: ContourCategory }
    | { kind: 'theme'; item: ContourTheme }
    | { kind: 'stage'; item: ContourStage }
    | null
  >(null);

  const toastTimer = useRef<number | undefined>(undefined);

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current;
    try {
      const [summary, categories] = await Promise.all([getContourSummary(), getContourCategories(categoryPage)]);
      let categoryItems = categories.items;
      let loadedThemeTotal = 0;
      let loadedStageTotal = 0;
      let focusedPage = stagePage;
      if (view.kind !== 'root') {
        const [category, themes] = await Promise.all([
          getContourCategory(view.categoryId), getContourThemes(view.categoryId, themePage),
        ]);
        loadedThemeTotal = themes.total;
        let themeItems = themes.items;
        if (view.kind === 'theme') {
          const [theme, stages] = await Promise.all([
            getContourTheme(view.themeId), getContourStages(view.themeId, stagePage, undefined, highlightStageId ?? undefined),
          ]);
          loadedStageTotal = stages.total;
          focusedPage = stages.page;
          const loadedTheme = { ...theme, stages: stages.items };
          themeItems = themeItems.some((item) => item.id === theme.id)
            ? themeItems.map((item) => item.id === theme.id ? loadedTheme : item)
            : [...themeItems, loadedTheme];
        }
        const loadedCategory = { ...category, themes: themeItems };
        categoryItems = categoryItems.some((item) => item.id === category.id)
          ? categoryItems.map((item) => item.id === category.id ? loadedCategory : item)
          : [...categoryItems, loadedCategory];
      }
      if (generation !== loadGeneration.current) return false;
      if (highlightStageId && focusedPage !== stagePage) setStagePage(focusedPage);
      setOverview({ ...summary, categories: categoryItems });
      setCategoryTotal(categories.total);
      setThemeTotal(loadedThemeTotal);
      setStageTotal(loadedStageTotal);
      setLoadState('success');
      return true;
    } catch (error) {
      if (generation !== loadGeneration.current) return false;
      if (error instanceof ApiError && error.status === 404 && view.kind !== 'root') {
        setView(view.kind === 'theme' ? { kind: 'category', categoryId: view.categoryId } : { kind: 'root' });
        return false;
      }
      setLoadState('error');
      return false;
    }
  }, [categoryPage, themePage, stagePage, view, highlightStageId]);

  useEffect(() => {
    setLoadState('loading');
    void load();
  }, [load]);

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  const showToast = useCallback((message: string) => {
    setToast(message);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 1800);
  }, []);

  /** Единая обёртка: показывает ошибку, но не теряет введённые данные. */
  const run = useCallback(
    async (action: () => Promise<unknown>, successMessage?: string) => {
      if (busyRef.current) return false;
      busyRef.current = true;
      setSaving(true);
      setErrorText(null);
      try {
        await action();
        await load();
        if (successMessage) showToast(successMessage);
        return true;
      } catch (error) {
        await load();
        setErrorText(error instanceof ApiError && error.status === 409
          ? 'Данные изменились. Обновите страницу и повторите действие.'
          : 'Не удалось сохранить изменения. Попробуйте ещё раз.');
        return false;
      } finally {
        busyRef.current = false;
        setSaving(false);
      }
    },
    [load, showToast],
  );

  // Закрытие меню действий по клику вне его.
  useEffect(() => {
    if (!openMenuId) return;
    const close = () => setOpenMenuId(null);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [openMenuId]);

  const categories = overview?.categories ?? [];
  const activeCategory = useMemo(
    () => (view.kind === 'root' ? null : categories.find((item) => item.id === view.categoryId) ?? null),
    [categories, view],
  );
  const activeTheme = useMemo(
    () => (view.kind === 'theme' ? activeCategory?.themes.find((item) => item.id === view.themeId) ?? null : null),
    [activeCategory, view],
  );

  useEffect(() => setEditMode(false), [view.kind, activeTheme?.id, activeCategory?.id]);

  useEffect(() => {
    if (loadState !== 'success') return;
    if (categoryPage > 1 && categoryPage > Math.ceil(categoryTotal / contourPageSize)) setCategoryPage(categoryPage - 1);
    if (view.kind !== 'root' && themePage > 1 && themePage > Math.ceil(themeTotal / contourPageSize)) setThemePage(themePage - 1);
    if (view.kind === 'theme' && stagePage > 1 && stagePage > Math.ceil(stageTotal / contourPageSize)) setStagePage(stagePage - 1);
  }, [loadState, categoryPage, categoryTotal, themePage, themeTotal, stagePage, stageTotal, view.kind]);

  useEffect(() => {
    const generation = ++searchGeneration.current;
    if (!searchOpen || !query.trim()) {
      setSearchResults(null);
      setSearchBusy(false);
      return;
    }
    setSearchBusy(true);
    setSearchError(false);
    const timer = window.setTimeout(() => {
      void searchContour(query.trim(), searchPage).then((results) => {
        if (generation === searchGeneration.current) setSearchResults(results);
      }).catch(() => {
        if (generation === searchGeneration.current) setSearchError(true);
      }).finally(() => {
        if (generation === searchGeneration.current) setSearchBusy(false);
      });
    }, 250);
    return () => window.clearTimeout(timer);
  }, [query, searchPage, searchOpen, searchRefresh]);

  const toggleStage = useCallback(
    async (stage: ContourStage) => {
      const next = !stage.completed;
      await run(() => updateContourStage(stage.id, { completed: next, expectedVersion: stage.version }),
        next ? 'Этап выполнен' : 'Отметка снята');
    },
    [run],
  );

  const moveStage = useCallback(
    async (theme: ContourTheme, stageId: string, direction: -1 | 1) => {
      const expectedIds = theme.stages.map((item) => item.id);
      const ids = [...expectedIds];
      const index = ids.indexOf(stageId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= ids.length) return;
      [ids[index], ids[target]] = [ids[target], ids[index]];
      await run(() => reorderContourStagePage(theme.id, ids, expectedIds, stagePage));
    },
    [run, stagePage],
  );

  /** Перемещение категории в общем списке (§26). */
  const moveCategory = useCallback(
    async (categoryId: string, direction: -1 | 1) => {
      const expectedIds = categories.map((item) => item.id);
      const ids = [...expectedIds];
      const index = ids.indexOf(categoryId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= ids.length) return;
      [ids[index], ids[target]] = [ids[target], ids[index]];
      await run(() => reorderContourCategoryPage(ids, expectedIds, categoryPage));
    },
    [categories, run, categoryPage],
  );

  /** Перемещение темы внутри категории (§26). */
  const moveTheme = useCallback(
    async (category: ContourCategory, themeId: string, direction: -1 | 1) => {
      const expectedIds = category.themes.map((item) => item.id);
      const ids = [...expectedIds];
      const index = ids.indexOf(themeId);
      const target = index + direction;
      if (index < 0 || target < 0 || target >= ids.length) return;
      [ids[index], ids[target]] = [ids[target], ids[index]];
      await run(() => reorderContourThemePage(category.id, ids, expectedIds, themePage));
    },
    [run, themePage],
  );

  if (loadState === 'loading') return <LoadingState label="Загружаем контур…" />;
  if (loadState === 'error') {
    return (
      <StatePanel
        kind="error"
        title="Контур недоступен"
        description="Не удалось загрузить данные контура."
        actionLabel="Повторить"
        onAction={() => void load()}
      />
    );
  }

  const overallCompleted = overview?.completedStages ?? 0;
  const overallTotal = overview?.totalStages ?? 0;

  // ------------------------------------------------------------- поиск
  if (searchOpen) {
    return (
      <div className="page-stack contour-page" aria-busy={saving}>
        <header className="contour-header">
          <button
            className="contour-header__back"
            type="button"
            onClick={() => {
              setSearchOpen(false);
              setQuery('');
              setSearchPage(1);
            }}
            aria-label="Закрыть поиск"
          >
            <ArrowLeft size={22} strokeWidth={1.8} aria-hidden="true" />
          </button>
          <div>
            <h1>Поиск</h1>
          </div>
        </header>

        <label className="search-field contour-search-field">
          <Search size={18} aria-hidden="true" />
          <span className="sr-only">Поиск по контуру</span>
          <input
            type="search"
            value={query}
            autoFocus
            onChange={(event) => { setQuery(event.target.value); setSearchPage(1); }}
            placeholder="Название или описание"
          />
          {query && (
            <button className="contour-search-clear" type="button" onClick={() => setQuery('')} aria-label="Очистить поиск">
              <X size={18} aria-hidden="true" />
            </button>
          )}
        </label>

        {!query.trim() ? (
          <p className="contour-hint">Введите запрос, чтобы найти категории, темы и этапы.</p>
        ) : searchBusy ? (
          <p className="contour-hint" role="status">Ищем…</p>
        ) : searchError ? (
          <div className="contour-empty" role="alert">
            <p>Поиск недоступен.</p>
            <button className="button button--secondary" type="button" onClick={() => setSearchRefresh((value) => value + 1)}>
              Повторить
            </button>
          </div>
        ) : searchResults?.items.length === 0 ? (
          <div className="contour-empty" role="status"><p>По вашему запросу ничего не найдено.</p></div>
        ) : searchResults ? (
          <>
          <ul className="contour-list" aria-label="Результаты поиска">
            {searchResults.items.map((result) => (
              <li key={`${result.kind}-${result.id}`}>
                <button
                  className="contour-row contour-row--search"
                  type="button"
                  onClick={() => {
                    setSearchOpen(false);
                    setQuery('');
                    setSearchPage(1);
                    if (result.themeId) {
                      setThemePage(1);
                      setStagePage(1);
                      setView({ kind: 'theme', categoryId: result.categoryId, themeId: result.themeId });
                      if (result.kind === 'stage') {
                        setHighlightStageId(result.id);
                        window.setTimeout(() => setHighlightStageId(null), 2400);
                      }
                    } else {
                      setThemePage(1);
                      setView({ kind: 'category', categoryId: result.categoryId });
                    }
                  }}
                >
                  <span className="contour-row__icon" aria-hidden="true">
                    {'📄'}
                  </span>
                  <span className="contour-row__copy">
                    <strong><HighlightMatch text={result.name} query={query} /></strong>
                    <span>
                      {result.kind === 'stage'
                        ? `${result.categoryName} → ${result.themeName}`
                        : result.kind === 'theme'
                          ? result.categoryName
                          : 'Категория'}
                    </span>
                    {result.description ? <span className="contour-row__description">{result.description}</span> : null}
                  </span>
                  <ChevronRight size={20} strokeWidth={1.8} aria-hidden="true" className="contour-row__chevron" />
                </button>
              </li>
            ))}
          </ul>
          <PageControls page={searchPage} total={searchResults.total} pageSize={40} onPage={setSearchPage} />
          </>
        ) : null}
      </div>
    );
  }

  // ------------------------------------------------------------- экран темы
  if (view.kind === 'theme' && activeTheme && activeCategory) {
    const theme = activeTheme;
    return (
      <div className="page-stack contour-page" aria-busy={saving}>
        <header className="contour-header contour-header--theme">
          <button
            className="contour-header__back"
            type="button"
            onClick={() => { setThemePage(1); setView({ kind: 'category', categoryId: activeCategory.id }); }}
            aria-label="Назад к темам"
          >
            <ArrowLeft size={22} strokeWidth={1.8} aria-hidden="true" />
          </button>
          <div>
            <h1>
              {theme.icon ? <span aria-hidden="true">{theme.icon} </span> : null}
              {theme.name}
            </h1>
            <p>
              {formatContourProgress(theme.completedStages, theme.totalStages)}
            </p>
          </div>
          <div className="contour-header__actions">
            {theme.totalStages > 0 && (
              <button
                className="contour-header__text-button"
                type="button"
                onClick={() => setEditMode((current) => !current)}
              >
                {editMode ? 'Готово' : 'Изменить'}
              </button>
            )}
            <button
              className="contour-header__add"
              type="button"
              onClick={() => setStageDialog({ mode: 'create' })}
              aria-label="Добавить этап"
            >
              <Plus size={22} strokeWidth={1.8} aria-hidden="true" />
            </button>
          </div>
        </header>

        {theme.totalStages > 0 && <ProgressBar completed={theme.completedStages} total={theme.totalStages} />}
        {theme.description ? <p className="contour-description">{theme.description}</p> : null}
        {errorText ? <p className="contour-error" role="alert">{errorText}</p> : null}
        {saving ? <p className="contour-saving" role="status">Сохраняем…</p> : null}

        {theme.stages.length === 0 ? (
          <div className="contour-empty">
            <p>Добавьте первый этап.</p>
            <button className="button button--primary" type="button" onClick={() => setStageDialog({ mode: 'create' })}>
              <Plus size={18} aria-hidden="true" />
              Добавить этап
            </button>
          </div>
        ) : (
          <StageList
            theme={theme}
            startIndex={(stagePage - 1) * contourPageSize}
            editMode={editMode}
            disabled={saving}
            highlightStageId={highlightStageId}
            openMenuId={openMenuId}
            onToggle={(stage) => void toggleStage(stage)}
            onMove={(stageId, direction) => void moveStage(theme, stageId, direction)}
            onReorder={(ids) => void run(() => reorderContourStagePage(theme.id, ids,
              theme.stages.map((item) => item.id), stagePage))}
            onOpenMenu={setOpenMenuId}
            onEdit={(stage) => setStageDialog({ mode: 'edit', item: stage })}
            onDelete={(stage) => setConfirm({ kind: 'stage', item: stage })}
          />
        )}
        <PageControls page={stagePage} total={stageTotal} pageSize={contourPageSize} onPage={setStagePage} />

        {renderDialogs()}
        {toast ? <div className="contour-toast" role="status">{toast}</div> : null}
      </div>
    );
  }

  // --------------------------------------------------------- экран категории
  if (view.kind === 'category' && activeCategory) {
    const category = activeCategory;
    return (
      <div className="page-stack contour-page" aria-busy={saving}>
        <header className="contour-header">
          <button
            className="contour-header__back"
            type="button"
            onClick={() => { setCategoryPage(1); setView({ kind: 'root' }); }}
            aria-label="Назад к категориям"
          >
            <ArrowLeft size={22} strokeWidth={1.8} aria-hidden="true" />
          </button>
          <div>
            <h1>
              {category.icon ? <span aria-hidden="true">{category.icon} </span> : null}
              {category.name}
            </h1>
            <p>{formatThemeCount(category.totalThemes)}</p>
          </div>
          <div className="contour-header__actions">
            <button
              className="contour-header__add"
              type="button"
              onClick={() => setThemeDialog({ mode: 'create' })}
              aria-label="Добавить тему"
            >
              <Plus size={22} strokeWidth={1.8} aria-hidden="true" />
            </button>
          </div>
        </header>

        {errorText ? <p className="contour-error" role="alert">{errorText}</p> : null}
        {saving ? <p className="contour-saving" role="status">Сохраняем…</p> : null}

        {category.themes.length === 0 ? (
          <div className="contour-empty">
            <p>В этой категории пока нет тем.</p>
            <button className="button button--primary" type="button" onClick={() => setThemeDialog({ mode: 'create' })}>
              <Plus size={18} aria-hidden="true" />
              Добавить тему
            </button>
          </div>
        ) : (
          <ul className="contour-list" aria-label="Темы">
            {category.themes.map((theme, index) => (
              <li key={theme.id} className="contour-row-wrap">
                <button
                  className="contour-row"
                  type="button"
                  aria-label={`Открыть тему: ${theme.name}`}
                  onClick={() => { setStagePage(1); setView({ kind: 'theme', categoryId: category.id, themeId: theme.id }); }}
                >
                  <span className="contour-row__icon" aria-hidden="true">{theme.icon || '🔧'}</span>
                  <span className="contour-row__copy">
                    <strong>{theme.name}</strong>
                    <span>
                      {formatContourProgress(theme.completedStages, theme.totalStages)}
                    </span>
                    {theme.description ? <span className="contour-row__description">{theme.description}</span> : null}
                    <ProgressBar completed={theme.completedStages} total={theme.totalStages} />
                  </span>
                  <ChevronRight size={20} strokeWidth={1.8} aria-hidden="true" className="contour-row__chevron" />
                </button>

                <span className="contour-menu-anchor">
                  <button
                    className="contour-row__menu"
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenMenuId(openMenuId === theme.id ? null : theme.id);
                    }}
                    aria-label={`Действия: ${theme.name}`}
                    aria-expanded={openMenuId === theme.id}
                  >
                    <MoreVertical size={18} aria-hidden="true" />
                  </button>
                  {openMenuId === theme.id && (
                    <div className="contour-menu" role="menu" onClick={(event) => event.stopPropagation()}>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setOpenMenuId(null);
                          setThemeDialog({ mode: 'edit', item: theme });
                        }}
                      >
                        <Pencil size={16} aria-hidden="true" />
                        Изменить
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={index === 0}
                        onClick={() => {
                          setOpenMenuId(null);
                          void moveTheme(category, theme.id, -1);
                        }}
                      >
                        <ChevronUp size={16} aria-hidden="true" />
                        Переместить выше
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={index === category.themes.length - 1}
                        onClick={() => {
                          setOpenMenuId(null);
                          void moveTheme(category, theme.id, 1);
                        }}
                      >
                        <ChevronDown size={16} aria-hidden="true" />
                        Переместить ниже
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setOpenMenuId(null);
                          void (async () => {
                            let copyId = '';
                            const done = await run(async () => {
                              copyId = (await duplicateContourTheme(theme.id)).id;
                            }, 'Тема продублирована');
                            if (done && copyId) { setStagePage(1); setView({ kind: 'theme', categoryId: category.id, themeId: copyId }); }
                          })();
                        }}
                      >
                        <Copy size={16} aria-hidden="true" />
                        Дублировать
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="is-danger"
                        onClick={() => {
                          setOpenMenuId(null);
                          setConfirm({ kind: 'theme', item: theme });
                        }}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                        Удалить
                      </button>
                    </div>
                  )}
                </span>
              </li>
            ))}
          </ul>
        )}
        <PageControls page={themePage} total={themeTotal} pageSize={contourPageSize} onPage={setThemePage} />

        {renderDialogs()}
        {toast ? <div className="contour-toast" role="status">{toast}</div> : null}
      </div>
    );
  }

  // ------------------------------------------------------------ главный экран
  return (
    <div className="page-stack contour-page" aria-busy={saving}>
      <header className="contour-header contour-header--root">
        <div>
          <h1>Контур</h1>
        </div>
        <div className="contour-header__actions">
          <button
            className="contour-header__icon-button"
            type="button"
            onClick={() => setSearchOpen(true)}
            aria-label="Поиск по контуру"
          >
            <Search size={20} strokeWidth={1.8} aria-hidden="true" />
          </button>
          <button
            className="button button--primary contour-header__create"
            type="button"
            onClick={() => setCategoryDialog({ mode: 'create' })}
          >
            <Plus size={18} strokeWidth={1.8} aria-hidden="true" />
            Создать категорию
          </button>
        </div>
      </header>

      {errorText ? <p className="contour-error" role="alert">{errorText}</p> : null}
      {saving ? <p className="contour-saving" role="status">Сохраняем…</p> : null}

      <section className="contour-overall" aria-label="Общий прогресс">
        <span className="contour-overall__label">Общий прогресс</span>
        <strong className="contour-overall__value">{contourPercent(overallCompleted, overallTotal)}%</strong>
        <ProgressBar completed={overallCompleted} total={overallTotal} />
        <span className="contour-overall__count">{formatContourProgress(overallCompleted, overallTotal)}</span>
      </section>

      {categories.length === 0 ? (
        <div className="contour-empty contour-empty--root">
          <p>Контур пока пуст</p>
          <p className="contour-empty__hint">
            Создайте первую категорию, чтобы начать работу.
          </p>
          <button className="button button--primary" type="button" onClick={() => setCategoryDialog({ mode: 'create' })}>
            <Plus size={18} aria-hidden="true" />
            Создать категорию
          </button>
        </div>
      ) : (
        <>
          <h2 className="contour-section-title">Категории</h2>

          <ul className="contour-list" aria-label="Категории">
            {categories.map((category, index) => (
              <li key={category.id} className="contour-row-wrap">
                <button
                  className="contour-row"
                  type="button"
                  aria-label={`Открыть категорию: ${category.name}`}
                  onClick={() => { setThemePage(1); setView({ kind: 'category', categoryId: category.id }); }}
                >
                  <span className="contour-row__icon" aria-hidden="true">{category.icon || '🏗'}</span>
                  <span className="contour-row__copy">
                    <strong>{category.name}</strong>
                    <span>
                      {formatContourProgress(category.completedStages, category.totalStages)}
                    </span>
                    <ProgressBar completed={category.completedStages} total={category.totalStages} />
                  </span>
                  <ChevronRight size={20} strokeWidth={1.8} aria-hidden="true" className="contour-row__chevron" />
                </button>

                <span className="contour-menu-anchor">
                  <button
                    className="contour-row__menu"
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation();
                      setOpenMenuId(openMenuId === category.id ? null : category.id);
                    }}
                    aria-label={`Действия: ${category.name}`}
                    aria-expanded={openMenuId === category.id}
                  >
                    <MoreVertical size={18} aria-hidden="true" />
                  </button>
                  {openMenuId === category.id && (
                    <div className="contour-menu" role="menu" onClick={(event) => event.stopPropagation()}>
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setOpenMenuId(null);
                          setCategoryDialog({ mode: 'edit', item: category });
                        }}
                      >
                        <Pencil size={16} aria-hidden="true" />
                        Изменить
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={index === 0}
                        onClick={() => {
                          setOpenMenuId(null);
                          void moveCategory(category.id, -1);
                        }}
                      >
                        <ChevronUp size={16} aria-hidden="true" />
                        Переместить выше
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        disabled={index === categories.length - 1}
                        onClick={() => {
                          setOpenMenuId(null);
                          void moveCategory(category.id, 1);
                        }}
                      >
                        <ChevronDown size={16} aria-hidden="true" />
                        Переместить ниже
                      </button>
                      <button
                        type="button"
                        role="menuitem"
                        className="is-danger"
                        onClick={() => {
                          setOpenMenuId(null);
                          setConfirm({ kind: 'category', item: category });
                        }}
                      >
                        <Trash2 size={16} aria-hidden="true" />
                        Удалить
                      </button>
                    </div>
                  )}
                </span>
              </li>
            ))}
          </ul>
          <PageControls page={categoryPage} total={categoryTotal} pageSize={contourPageSize} onPage={setCategoryPage} />
        </>
      )}

      {renderDialogs()}
      {toast ? <div className="contour-toast" role="status">{toast}</div> : null}
    </div>
  );

  // ------------------------------------------------------------- диалоги
  function renderDialogs() {
    return (
      <>
        {categoryDialog && (
          <CategoryDialog
            key={categoryDialog.item?.id ?? 'new-category'}
            mode={categoryDialog.mode}
            item={categoryDialog.item}
            errorText={errorText}
            onClose={() => {
              setCategoryDialog(null);
              setErrorText(null);
            }}
            onSubmit={async (name, icon) => {
              const done = await run(
                () =>
                  categoryDialog.mode === 'create'
                    ? createContourCategory({ name, icon })
                    : updateContourCategory(categoryDialog.item!.id, { name, icon }),
              );
              if (done) setCategoryDialog(null);
            }}
          />
        )}

        {themeDialog && activeCategory && (
          <ThemeDialog
            key={themeDialog.item?.id ?? 'new-theme'}
            mode={themeDialog.mode}
            item={themeDialog.item}
            errorText={errorText}
            onClose={() => {
              setThemeDialog(null);
              setErrorText(null);
            }}
            onSubmit={async (name, description, icon) => {
              const done = await run(
                () =>
                  themeDialog.mode === 'create'
                    ? createContourTheme({ categoryId: activeCategory.id, name, description, icon })
                    : updateContourTheme(themeDialog.item!.id, { name, description, icon }),
              );
              if (done) setThemeDialog(null);
            }}
          />
        )}

        {stageDialog && activeTheme && (
          <StageDialog
            key={stageDialog.item?.id ?? 'new-stage'}
            mode={stageDialog.mode}
            item={stageDialog.item}
            errorText={errorText}
            onDelete={
              stageDialog.mode === 'edit'
                ? () => {
                    const target = stageDialog.item!;
                    setStageDialog(null);
                    setConfirm({ kind: 'stage', item: target });
                  }
                : undefined
            }
            onClose={() => {
              setStageDialog(null);
              setErrorText(null);
            }}
            onSubmit={async (name, description, metadata) => {
              const done = await run(
                () =>
                  stageDialog.mode === 'create'
                    ? createContourStage({ themeId: activeTheme.id, name, description, ...metadata })
                    : updateContourStage(stageDialog.item!.id, {
                        name, description, ...metadata, expectedVersion: stageDialog.item!.version,
                      }),
              );
              if (done) setStageDialog(null);
            }}
          />
        )}

        {confirm && (
          <ContourModal
            title={
              confirm.kind === 'category'
                ? 'Удалить категорию?'
                : confirm.kind === 'theme'
                  ? 'Удалить тему?'
                  : 'Удалить этап?'
            }
            onClose={() => setConfirm(null)}
          >
            <div className="contour-dialog__body">
              <p className="contour-confirm__name">{confirm.item.name}</p>
              <p className="contour-confirm__warning">
                {confirm.kind === 'category'
                  ? `Вместе с категорией будут удалены все темы (${confirm.item.totalThemes}) и их этапы. Это действие нельзя отменить.`
                  : confirm.kind === 'theme'
                    ? `Будут удалены все этапы темы (${confirm.item.totalStages}). Это действие нельзя отменить.`
                    : 'Этап будет удалён. Это действие нельзя отменить.'}
              </p>
              {errorText ? <p className="contour-error" role="alert">{errorText}</p> : null}
            </div>
            <footer className="contour-dialog__footer">
              <button className="button button--secondary" type="button" onClick={() => setConfirm(null)}>
                Отмена
              </button>
              <button
                className="button button--danger"
                type="button"
                onClick={async () => {
                  const done = await run(() =>
                    confirm.kind === 'category'
                      ? deleteContourCategory(confirm.item.id)
                      : confirm.kind === 'theme'
                        ? deleteContourTheme(confirm.item.id)
                        : deleteContourStage(confirm.item.id),
                  );
                  if (done) setConfirm(null);
                }}
              >
                Удалить
              </button>
            </footer>
          </ContourModal>
        )}
      </>
    );
  }
}

/** Форма категории: название + иконка. */
function CategoryDialog({
  mode,
  item,
  errorText,
  onClose,
  onSubmit,
}: {
  mode: 'create' | 'edit';
  item?: ContourCategory;
  errorText: string | null;
  onClose: () => void;
  onSubmit: (name: string, icon: string) => Promise<void>;
}) {
  const [name, setName] = useState(item?.name ?? '');
  const [icon, setIcon] = useState(item?.icon || CATEGORY_ICONS[0]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <ContourModal title={mode === 'create' ? 'Новая категория' : 'Редактирование категории'} onClose={() => { if (!busy) onClose(); }}>
      <form
        className="contour-dialog__body"
        onSubmit={async (event) => {
          event.preventDefault();
          if (busy) return;
          if (!name.trim()) {
            setLocalError('Введите название.');
            return;
          }
          setLocalError(null);
          setBusy(true);
          await onSubmit(name.trim(), icon);
          setBusy(false);
        }}
      >
        <label className="field-group">
          <span>Название</span>
          <input
            type="text"
            value={name}
            autoFocus
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
            placeholder="Инженерные системы"
          />
        </label>

        <span className="field-group__label">Иконка</span>
        <IconPicker icons={CATEGORY_ICONS} value={icon} onChange={setIcon} />

        {(localError || errorText) && (
          <p className="contour-error" role="alert">{localError || errorText}</p>
        )}

        <footer className="contour-dialog__footer">
          <button className="button button--secondary" type="button" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? 'Сохраняем…' : mode === 'create' ? 'Создать' : 'Сохранить'}
          </button>
        </footer>
      </form>
    </ContourModal>
  );
}

/** Форма темы: название, описание, иконка. */
function ThemeDialog({
  mode,
  item,
  errorText,
  onClose,
  onSubmit,
}: {
  mode: 'create' | 'edit';
  item?: ContourTheme;
  errorText: string | null;
  onClose: () => void;
  onSubmit: (name: string, description: string, icon: string) => Promise<void>;
}) {
  const [name, setName] = useState(item?.name ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [icon, setIcon] = useState(item?.icon || THEME_ICONS[0]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <ContourModal title={mode === 'create' ? 'Новая тема' : 'Редактирование темы'} onClose={() => { if (!busy) onClose(); }}>
      <form
        className="contour-dialog__body"
        onSubmit={async (event) => {
          event.preventDefault();
          if (busy) return;
          if (!name.trim()) {
            setLocalError('Введите название.');
            return;
          }
          setLocalError(null);
          setBusy(true);
          await onSubmit(name.trim(), description.trim(), icon);
          setBusy(false);
        }}
      >
        <label className="field-group">
          <span>Название</span>
          <input
            type="text"
            value={name}
            autoFocus
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
            placeholder="Отопление"
          />
        </label>

        <label className="field-group">
          <span>
            Описание<small> — необязательно</small>
          </span>
          <textarea
            value={description}
            rows={2}
            maxLength={1000}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Система отопления здания."
          />
        </label>

        <span className="field-group__label">Иконка</span>
        <IconPicker icons={THEME_ICONS} value={icon} onChange={setIcon} />

        {(localError || errorText) && (
          <p className="contour-error" role="alert">{localError || errorText}</p>
        )}

        <footer className="contour-dialog__footer">
          <button className="button button--secondary" type="button" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? 'Сохраняем…' : mode === 'create' ? 'Создать' : 'Сохранить'}
          </button>
        </footer>
      </form>
    </ContourModal>
  );
}

type StageFormMetadata = Partial<ContourTaskMetadata> & { clearDeadline?: boolean; clearAssignee?: boolean };

const localDateTime = (value?: string | null) => {
  if (!value) return '';
  const date = new Date(value);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
};

/** Редактор задачи и её сохранённые комментарии/история. */
function StageDialog({
  mode,
  item,
  errorText,
  onClose,
  onSubmit,
  onDelete,
}: {
  mode: 'create' | 'edit';
  item?: ContourStage;
  errorText: string | null;
  onClose: () => void;
  onSubmit: (name: string, description: string, metadata: StageFormMetadata) => Promise<void>;
  onDelete?: () => void;
}) {
  const [name, setName] = useState(item?.name ?? '');
  const [description, setDescription] = useState(item?.description ?? '');
  const [status, setStatus] = useState<ContourTaskMetadata['status']>(item?.status ?? 'Todo');
  const [priority, setPriority] = useState<ContourTaskMetadata['priority']>(item?.priority ?? 'Normal');
  const [deadline, setDeadline] = useState(localDateTime(item?.deadlineUtc));
  const [assigneeId, setAssigneeId] = useState(item?.assigneeId ?? '');
  const [assignees, setAssignees] = useState<{ id: string; name: string }[]>([]);
  const [comments, setComments] = useState<ContourPage<ContourComment> | null>(null);
  const [history, setHistory] = useState<ContourPage<ContourTaskHistory> | null>(null);
  const [comment, setComment] = useState('');
  const [activityError, setActivityError] = useState<string | null>(null);
  const [activityBusy, setActivityBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let active = true;
    void getContourAssignees().then((rows) => { if (active) setAssignees(rows); })
      .catch(() => { if (active) setActivityError('Не удалось загрузить исполнителя.'); });
    if (item) {
      void Promise.all([getContourComments(item.id), getContourTaskHistory(item.id)]).then(([commentPage, historyPage]) => {
        if (active) { setComments(commentPage); setHistory(historyPage); }
      }).catch(() => { if (active) setActivityError('Не удалось загрузить комментарии и историю.'); });
    }
    return () => { active = false; };
  }, [item]);

  return (
    <ContourModal title={mode === 'create' ? 'Новый этап' : 'Редактирование этапа'} onClose={() => { if (!busy) onClose(); }}>
      <form
        className="contour-dialog__body"
        onSubmit={async (event) => {
          event.preventDefault();
          if (busy) return;
          if (!name.trim()) {
            setLocalError('Введите название.');
            return;
          }
          setLocalError(null);
          setBusy(true);
          await onSubmit(name.trim(), description.trim(), {
            status, priority,
            ...(deadline ? { deadlineUtc: new Date(deadline).toISOString() } : { clearDeadline: true }),
            ...(assigneeId ? { assigneeId } : { clearAssignee: true }),
          });
          setBusy(false);
        }}
      >
        <label className="field-group">
          <span>Название</span>
          <input
            type="text"
            value={name}
            autoFocus
            maxLength={200}
            onChange={(event) => setName(event.target.value)}
            placeholder="Подключение системы"
          />
        </label>

        <div className="contour-task-fields">
          <label className="field-group">
            <span>Статус</span>
            <select value={status} onChange={(event) => setStatus(event.target.value as ContourTaskMetadata['status'])}>
              <option value="Todo">К выполнению</option>
              <option value="InProgress">В работе</option>
              <option value="Blocked">Заблокировано</option>
              <option value="Done">Выполнено</option>
              <option value="Cancelled">Отменено</option>
            </select>
          </label>
          <label className="field-group">
            <span>Приоритет</span>
            <select value={priority} onChange={(event) => setPriority(event.target.value as ContourTaskMetadata['priority'])}>
              <option value="Low">Низкий</option>
              <option value="Normal">Обычный</option>
              <option value="High">Высокий</option>
              <option value="Urgent">Срочный</option>
            </select>
          </label>
          <label className="field-group">
            <span>Срок<small> — необязательно</small></span>
            <input type="datetime-local" value={deadline} onChange={(event) => setDeadline(event.target.value)} />
          </label>
          <label className="field-group">
            <span>Исполнитель<small> — необязательно</small></span>
            <select value={assigneeId} onChange={(event) => setAssigneeId(event.target.value)}>
              <option value="">Не назначен</option>
              {assignees.map((person) => <option value={person.id} key={person.id}>{person.name}</option>)}
            </select>
          </label>
        </div>

        <label className="field-group">
          <span>
            Описание<small> — необязательно</small>
          </span>
          <textarea
            value={description}
            rows={2}
            maxLength={1000}
            onChange={(event) => setDescription(event.target.value)}
            placeholder="Подключить систему отопления к основной магистрали."
          />
        </label>

        {(localError || errorText) && (
          <p className="contour-error" role="alert">{localError || errorText}</p>
        )}

        <footer className="contour-dialog__footer">
          {onDelete && (
            <button className="button button--danger contour-dialog__delete" type="button" onClick={onDelete} disabled={busy}>
              Удалить
            </button>
          )}
          <button className="button button--secondary" type="button" onClick={onClose} disabled={busy}>
            Отмена
          </button>
          <button className="button button--primary" type="submit" disabled={busy}>
            {busy ? 'Сохраняем…' : mode === 'create' ? 'Добавить' : 'Сохранить'}
          </button>
        </footer>
      </form>
      {item && (
        <div className="contour-task-activity">
          <h3>Комментарии</h3>
          {activityError && <p className="contour-error" role="alert">{activityError}</p>}
          {comments ? comments.items.length ? (
            <ul className="contour-task-activity__list">
              {comments.items.map((entry) => <li key={entry.id}>
                <strong>{entry.authorName ?? 'Пользователь'}</strong>
                <time dateTime={entry.createdAt}>{new Date(entry.createdAt).toLocaleString('ru-RU')}</time>
                <p>{entry.body}</p>
                <button type="button" disabled={activityBusy} onClick={async () => {
                  setActivityBusy(true); setActivityError(null);
                  try {
                    await deleteContourComment(item.id, entry.id);
                    setComments(await getContourComments(item.id, comments.page));
                    setHistory(await getContourTaskHistory(item.id, history?.page ?? 1));
                  } catch { setActivityError('Не удалось удалить комментарий.'); }
                  finally { setActivityBusy(false); }
                }}>Удалить комментарий</button>
              </li>)}
            </ul>
          ) : <p>Комментариев пока нет.</p> : <p role="status">Загружаем комментарии…</p>}
          <form onSubmit={async (event) => {
            event.preventDefault();
            if (!comment.trim() || activityBusy) return;
            setActivityBusy(true); setActivityError(null);
            try {
              await createContourComment(item.id, comment.trim());
              setComment('');
              setComments(await getContourComments(item.id, 1));
              setHistory(await getContourTaskHistory(item.id, history?.page ?? 1));
            } catch { setActivityError('Не удалось добавить комментарий.'); }
            finally { setActivityBusy(false); }
          }}>
            <label className="field-group"><span>Новый комментарий</span>
              <textarea value={comment} maxLength={4000} rows={2} onChange={(event) => setComment(event.target.value)} />
            </label>
            <button className="button button--secondary" type="submit" disabled={activityBusy || !comment.trim()}>Добавить комментарий</button>
          </form>
          {comments && <PageControls label="Страницы комментариев" page={comments.page} total={comments.total} pageSize={comments.pageSize}
            onPage={(page) => { void getContourComments(item.id, page).then(setComments).catch(() => setActivityError('Не удалось загрузить комментарии.')); }} />}
          <h3>История</h3>
          {history ? history.items.length ? <ul className="contour-task-activity__list">
            {history.items.map((entry) => <li key={entry.id}>
              <strong>{entry.actorName ?? 'Пользователь'}</strong> · {entry.action}{' '}
              <time dateTime={entry.createdAt}>{new Date(entry.createdAt).toLocaleString('ru-RU')}</time>
              <details><summary>Подробности</summary><pre>{entry.changes}</pre></details>
            </li>)}
          </ul> : <p>Изменений пока нет.</p> : <p role="status">Загружаем историю…</p>}
          {history && <PageControls label="Страницы истории" page={history.page} total={history.total} pageSize={history.pageSize}
            onPage={(page) => { void getContourTaskHistory(item.id, page).then(setHistory).catch(() => setActivityError('Не удалось загрузить историю.')); }} />}
        </div>
      )}
    </ContourModal>
  );
}
